/**
 * Instalação escopada do Google Tag Manager / Google tag (gtag.js).
 *
 * O frontend é uma SPA (CRA) que contém a landing (marketing) E o app logado (/app/*).
 * Para carregar o GTM APENAS nas rotas de landing, a página Landing chama installGtm()
 * no mount — em vez de colocar a tag no public/index.html, que valeria para o app todo.
 *
 * Equivale ao snippet oficial de <head> (loader gtm.js + dataLayer) e ao <noscript>
 * de <body>, porém montado via DOM só na página que o invoca.
 *
 * O GA4 (ID_GA4) roda nos DOIS lados: na landing (aquisição) e no app logado, montado
 * pelo Layout de /app/*, para termos métricas de uso de quem já é cliente.
 */

/** Google Ads — conversão de lead do formulário de cadastro. */
export const ID_GOOGLE_ADS = "AW-18328782547";
/** Rótulo da conversão de lead (Google Ads). */
export const CONVERSAO_LEAD_GOOGLE_ADS = `${ID_GOOGLE_ADS}/6JACCPjKy9EcENON7KNE`;
/** Container do GTM — só nas rotas de landing. */
export const ID_GTM_LANDING = "GTM-54Q244QZ";
/** Propriedade do Google Analytics 4 — landing + app logado. */
export const ID_GA4 = "G-Z8J1DSZD4M";

export function installGtm(containerId: string): void {
  if (typeof document === "undefined") return;
  // Idempotente: evita duplicar em re-render / React.StrictMode / troca de rota da landing.
  if (document.getElementById(`gtm-loader-${containerId}`)) return;

  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = `gtm-loader-${containerId}`;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.insertBefore(script, document.head.firstChild);

  // <noscript> por fidelidade ao snippet (inerte numa SPA que exige JS).
  if (!document.getElementById(`gtm-ns-${containerId}`)) {
    const ns = document.createElement("noscript");
    ns.id = `gtm-ns-${containerId}`;
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    ns.appendChild(iframe);
    document.body.insertBefore(ns, document.body.firstChild);
  }
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// IDs já configurados nesta sessão de página. Um segundo gtag('config', id) para o
// mesmo ID reenviaria page_view — o que duplicaria a visita de quem entra pela
// landing e depois loga (Landing e Layout chamam installGoogleTag).
const idsConfigurados = new Set<string>();

export interface OpcoesGoogleTag {
  /**
   * Deixe false onde a SPA manda page_view na mão (o app logado, via trackPageView).
   * Sem isso, o config manda o page_view da rota atual e o efeito de rota manda
   * outro igual — a mesma tela contaria duas vezes.
   */
  enviarPageView?: boolean;
}

export function installGoogleTag(configId: string, opcoes: OpcoesGoogleTag = {}): void {
  if (typeof document === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };

  if (!document.getElementById(`gtag-loader-${configId}`)) {
    const script = document.createElement("script");
    script.id = `gtag-loader-${configId}`;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${configId}`;
    document.head.insertBefore(script, document.head.firstChild);
  }

  if (idsConfigurados.has(configId)) return;
  idsConfigurados.add(configId);

  window.gtag("js", new Date());
  if (opcoes.enviarPageView === false) {
    window.gtag("config", configId, { send_page_view: false });
    return;
  }
  window.gtag("config", configId);
}

export function trackGoogleAdsLeadConversion(): void {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", "conversion", {
    send_to: CONVERSAO_LEAD_GOOGLE_ADS,
    value: 1.0,
    currency: "BRL",
  });
}

/**
 * page_view manual do app logado. Numa SPA a troca de rota não recarrega a página,
 * então sem isso o GA4 registraria só a primeira tela e o resto da sessão sumiria.
 */
export function trackPageView(caminho: string, titulo?: string): void {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("event", "page_view", {
    page_path: caminho,
    page_location: window.location.href,
    page_title: titulo || document.title,
  });
}

export interface UsuarioAnalytics {
  usuarioId?: number | null;
  empresaId?: number | null;
  perfil?: string | null;
  plano?: string | null;
  modoVisualizacao?: string | null;
}

/**
 * Amarra a sessão do GA4 à conta logada — sem PII: só IDs numéricos e rótulos de
 * plano/perfil. Nome, e-mail e telefone NUNCA vão para o Analytics (política do GA4).
 * user_id permite juntar a mesma conta em navegadores diferentes; as user_properties
 * permitem segmentar uso por plano, perfil e modo (facilities x oficina).
 */
export function identificarUsuarioLogado(dados: UsuarioAnalytics): void {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("set", { user_id: dados.usuarioId ? String(dados.usuarioId) : undefined });
  window.gtag("set", "user_properties", {
    empresa_id: dados.empresaId ? String(dados.empresaId) : undefined,
    perfil: dados.perfil || undefined,
    plano: dados.plano || undefined,
    modo_visualizacao: dados.modoVisualizacao || undefined,
  });
}

/** Evento recomendado do GA4 para entrada na conta. */
export function trackLogin(metodo = "senha"): void {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "login", { method: metodo });
}
